import React, { Fragment, useState } from "react";
import { Col, Form, Button, Row, Alert, Modal} from "react-bootstrap";
import { MedicalLicense } from "../../Physician/Models/MedicalLicense";
import { deepCopy } from "../Utility/DeepCopy";
import { StateWithCountryLookupItem } from "../Models/StateWithCountryLookupItem";
import InputMask from 'react-input-mask';
import { getDateWithOffset } from "../Utility/GetDateWithOffset";
import { CountryId } from "../Enums/CountryId";

interface IProps {
    medicalLicense: MedicalLicense;
    states: Array<StateWithCountryLookupItem>;
    handleRemove: (medicalLicense: MedicalLicense) => void;
    updateMedicalLicense: (medicalLicense: MedicalLicense) => void;
}

export default function MedicalLicenseCard(props: IProps) {
    const [originalLicense, setOriginalLicense] = useState<MedicalLicense>(props.medicalLicense);
    const [updatedLicense, setUpdatedLicense] = useState<MedicalLicense>(props.medicalLicense);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState<boolean>(false);

    const updateLicenseNumber = (value: string) => {
        var updated = deepCopy(updatedLicense);
        updated.licenseNumber = value;
        setUpdatedLicense(updated);
    }

    const updateExpirationDate = (value: string) => {
        var updated = deepCopy(updatedLicense);
        updated.expirationDate = value;

        if (Date.parse(value)) {
            updated.expirationDate = getDateWithOffset(value).toJSON();
        }
        setUpdatedLicense(updated);
    }

    const updateState = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var state = props.states.find(_ => _.id == parseInt(e.target.value));
        if (!state) state = props.states.find(_ => _.name == e.target.value);
        var value = state ? state.id : null;

        var updated = deepCopy(updatedLicense);
        updated.stateId = value;
        setUpdatedLicense(updated);
    }

    const beginEdit = () => {
        var updated = deepCopy(updatedLicense);
        updated.isEditing = true;
        setUpdatedLicense(updated);
    }

    const undo = () => {
        updatedLicense.isNew
        ? props.handleRemove(updatedLicense)
        : setUpdatedLicense(originalLicense);
    }

    const save = () => {
        var updated = deepCopy(updatedLicense);
        updated.isEditing = false;
        updated.isNew = false;
        props.updateMedicalLicense(updated);
        setUpdatedLicense(updated);
        setOriginalLicense(updated);
    }

    const isLicenseActive = () => {
        return getDateWithOffset(updatedLicense.expirationDate) > new Date();
    }

    const formatDefaultValue = (dateString: string) => {
        if (!dateString) return "";

        var date = getDateWithOffset(dateString);
        if (date == null) return ""

        var year = date.getFullYear();

        var month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;

        var day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;

        return month + '/' + day + '/' + year;
    }

    const confirmDelete = async (confirmation: boolean) => {
        if(confirmation)
            props.handleRemove(updatedLicense);
        setShowConfirmDelete(false);
    }

    return (
        <Fragment>
            <Modal show={showConfirmDelete} onHide={()=> confirmDelete(false)}>
                <Modal.Body>Are you sure you want to delete this medical license?</Modal.Body>
                <Modal.Footer>
                    <React.Fragment>
                        <Button variant="pp-primary" onClick={() => confirmDelete(true)}>
                            Yes, Delete
                        </Button>
                        <Button variant="pp-secondary" onClick={() => confirmDelete(false)}>
                            No, Cancel
                        </Button>
                    </React.Fragment>
                </Modal.Footer>
            </Modal>
            <Row noGutters className="align-items-end">
                <Col xs={6} md={6} lg={6} xl={3} className="px-2">
                    <Form.Group controlId="licenseNumber">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">License Number</Form.Label>
                        {updatedLicense.isEditing ?
                            <Form.Control type="input"
                                required
                                onChange={(e) => updateLicenseNumber(e.target.value)}
                                defaultValue={updatedLicense.licenseNumber ? updatedLicense.licenseNumber : ""} />
                            :
                            <Form.Text>{updatedLicense.licenseNumber ? updatedLicense.licenseNumber : ""}</Form.Text>
                        }
                    </Form.Group>
                </Col>
                <Col xs={6} md={6} lg={6} xl={2} className="px-2">
                    <Form.Group controlId="stateId">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">State</Form.Label>
                        {updatedLicense.isEditing ?
                            <Form.Control as="select"
                                required
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { updateState(e); }}
                                defaultValue={updatedLicense.stateId ? updatedLicense.stateId : ""}>
                                <option hidden value="">Select a state...</option>
                                <optgroup label="USA">
                                    {props.states.filter(x => x.countryId === CountryId.Usa).map(x => (<option value={x.id} key={x.id}>{x.shortName}</option>))}
                                </optgroup>
                                <optgroup label="Canada">
                                    {props.states.filter(x => x.countryId === CountryId.Canada).map(x => (<option value={x.id} key={x.id}>{x.shortName}</option>))}
                                </optgroup>
                            </Form.Control>
                            :
                            <Form.Text>{props.states.find(x => x.id == updatedLicense.stateId)?.shortName}</Form.Text>
                        }
                    </Form.Group>
                </Col>
                <Col xs={5} md={5} lg={5} xl={2} className="px-2">
                    <Form.Group>
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Expiration</Form.Label>
                        {updatedLicense.isEditing ?
                            <InputMask mask="99/99/9999"
                                defaultValue={updatedLicense.expirationDate ? formatDefaultValue(updatedLicense.expirationDate) : null}
                                onChange={(e: any) => updateExpirationDate(e.target.value)}>
                                {(inputProps: any) => {
                                    return <Form.Control {...inputProps} type="input" />
                                }}
                            </InputMask>
                            :
                            <Form.Text>{getDateWithOffset(updatedLicense.expirationDate).toLocaleDateString('en-US')}</Form.Text>
                        }
                    </Form.Group>
                </Col>
                {updatedLicense.isEditing ?
                <Col xs={6} md={6} lg={6} xl={2} className="px-2">
                    <div className="mb-3 float-right">
                        <Button variant="pp-primary" className="mt-1" onClick={() => save()}><strong>Save</strong></Button>
                    </div>
                </Col> :
                <Col xs={6} md={6} lg={6} xl={2} className="px-2">
                    <Form.Group>
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Status</Form.Label>
                        {isLicenseActive() ?
                            <Form.Text>Active</Form.Text>
                            :
                            <Form.Text className="text-danger" hidden={updatedLicense.isEditing}>
                                <i className="font-weight-bolder las la-exclamation-circle"></i> Expired
                            </Form.Text>
                        }
                    </Form.Group>
                </Col>}
                <Col xs={12} md={12} lg={12} xl={3} className="px-2">
                    <div className="mb-3 float-right">
                        {!updatedLicense.isEditing && <Button variant="outline-pp-primary" className="mt-1" onClick={() => beginEdit()}><strong>Edit</strong></Button>}    
                        {(updatedLicense.isEditing || updatedLicense.isNew) && <Button variant="outline-dark" className="mt-1" onClick={() => undo()}><strong>Undo</strong></Button>}
                        {!updatedLicense.isEditing && <span>&nbsp;<Button variant="outline-pp-primary" className="mt-1" onClick={() => setShowConfirmDelete(true)}><strong>Delete</strong></Button></span>}
                    </div>
                </Col>
            </Row>
            <Row>
                {(props.medicalLicense.validationMessage?.length > 0) &&
                    <Col>
                    { props.medicalLicense.validationMessage.map(message => {
                        return <Alert variant="danger">{message}</Alert>
                    })

                    }
                    </Col>
                }
            </Row>
        <hr className="mt-0"/>
        </Fragment>
    );
}
